import { useCallback } from 'react';

import { useTransferToCause, useUserBalances } from '@givebacks/api/ledger';
import { AxiosError } from 'axios';
import {
  BalanceCard,
  ButtonMantine,
  CurrencyInput,
  Modal,
  ModalProps,
  Stack,
  centsToDollar,
  notifications,
} from 'ui';
import { useField } from 'ui/Form';

import { getErrorMessage_v2 } from '../../../../utils/errorMessage';
import {
  TransferAccountModalTitleWithBackButton,
  useTransferAccountsModalGroupProvider,
} from '../../../organisms/modals/TransferAccountsModalGroup/TransferAccountModalGroup.context';

const CashbackDonationsFlowDonationModalContent = () => {
  const { openModalById, meta } = useTransferAccountsModalGroupProvider({});
  const { data: balance, isLoading } = useUserBalances();
  const { mutate: transferToCause, isPending: isTransferLoading } = useTransferToCause();
  const field = useField({
    initialValue: 0,
    validate: (value) => {
      if (value < 500) {
        return `Donations must be $5.00 or more.`;
      }
      const availableBalance = balance?.available ?? 0;

      if (value > availableBalance) {
        return `You can’t donate more than your available Rewards balance (${centsToDollar(availableBalance)}).`;
      }
    },
  });

  const transfer = useCallback(async () => {
    const error = await field.validate();

    if (error) {
      return;
    }

    transferToCause(
      {
        amount: field.getValue(),
        causeId: meta?.causeId ?? '',
      },
      {
        onSuccess: () => {
          openModalById('ConfirmDonation');
        },
        onError: (e) => {
          const error = e as Error | AxiosError;
          notifications.show({
            message: getErrorMessage_v2(error, 'Donation failed'),
            variant: 'error',
          });
        },
      }
    );
  }, [field, meta?.causeId]);

  return (
    <Stack gap="lg">
      <BalanceCard
        available={balance?.available}
        pending={balance?.pending?.amount}
        isLoading={isLoading}
      />

      <CurrencyInput
        {...field.getInputProps()}
        disabled={isTransferLoading}
        placeholder="0.00"
        label="Amount"
      />

      <ButtonMantine onClick={() => void transfer()} loading={isTransferLoading}>
        Donate
      </ButtonMantine>
    </Stack>
  );
};

const CashbackDonationsFlowDonationModal = ({
  opened,
  onClose,
  modalProps,
}: CashbackDonationsFlowDonationModalProps) => {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <TransferAccountModalTitleWithBackButton>
          Make a Donation
        </TransferAccountModalTitleWithBackButton>
      }
      transitionProps={modalProps.transitionProps}
    >
      <CashbackDonationsFlowDonationModalContent />
    </Modal>
  );
};

type CashbackDonationsFlowDonationModalProps = {
  opened: boolean;
  onClose: () => void;
  modalProps: Pick<ModalProps, 'transitionProps'>;
};

const CashbackDonationsFlowDonationModalWrapper = () => {
  const { opened, props, close } = useTransferAccountsModalGroupProvider({
    id: 'DonateCashback',
  });

  return <CashbackDonationsFlowDonationModal opened={opened} onClose={close} modalProps={props} />;
};

export { CashbackDonationsFlowDonationModalWrapper as CashbackDonationsFlowDonationModal };
